import React, { useEffect, useState, useRef } from "react";
import { Phone, ChevronDown, Check } from "lucide-react";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";

/**
 * Reusable phone selector for section pages.
 * Loads phone accounts when mounted and updates context on selection.
 * Returns null when no accounts available.
 *
 * @param {Object} props
 * @param {boolean} [props.compact=false] - Compact mode
 * @param {string} [props.section] - Section name (e.g. "templates", "campaigns") to filter phones by agent permissions
 */
const PhoneSelector = ({ compact = false, section }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const {
    phoneAccounts,
    selectedPhoneId,
    setSelectedPhoneId,
    fetchPhoneAccounts,
    loading,
  } = usePhoneAccount();

  const selectedAccount = phoneAccounts.find((a) => a.phoneId === selectedPhoneId);

  useEffect(() => {
    fetchPhoneAccounts(section);
  }, [fetchPhoneAccounts, section]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  if (phoneAccounts.length === 0 && !loading) {
    return null;
  }

  const handleSelect = (account) => {
    setSelectedPhoneId(account.phoneId);
    window.dispatchEvent(new CustomEvent("wabaAccountChanged", { detail: account }));
    setIsOpen(false);
  };

  if (compact) {
    return (
      <div className="relative" ref={containerRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={loading}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-gray-800 bg-white border border-gray-200 rounded-md hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors disabled:opacity-50"
        >
          <Phone size={16} className="text-gray-500" />
          <span className="truncate max-w-[160px]">
            {loading ? "Loading..." : selectedAccount ? `${selectedAccount.phoneName || "Account"} • ${selectedAccount.phoneNumber}` : "Select phone"}
          </span>
          <ChevronDown size={14} className={`text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
        {isOpen && !loading && (
          <div className="absolute left-0 top-full mt-1.5 w-full min-w-[220px] bg-white rounded-md border border-gray-200 shadow-lg z-50 overflow-hidden">
            <div className="max-h-64 overflow-y-auto py-1.5">
              {phoneAccounts.map((account) => {
                const isSelected = account.phoneId === selectedPhoneId;
                return (
                  <button
                    key={account.phoneId}
                    type="button"
                    onClick={() => handleSelect(account)}
                    className={`w-full px-3 py-2.5 text-left flex items-center gap-3 transition-colors ${
                      isSelected ? "bg-emerald-50" : "hover:bg-gray-50"
                    }`}
                  >
                    <div className={`flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${isSelected ? "bg-emerald-100" : "bg-gray-100"}`}>
                      <Phone size={14} className={isSelected ? "text-emerald-700" : "text-gray-500"} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{account.phoneName || "Account"}</p>
                      <p className="text-xs text-gray-500 font-medium">{account.phoneNumber}</p>
                    </div>
                    {isSelected && <Check size={16} className="text-emerald-700 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className="w-full sm:max-w-sm flex items-center gap-3 px-4 py-3 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent transition-all disabled:opacity-50 text-left"
      >
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
          <Phone size={20} className="text-gray-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500">WhatsApp Number</p>
          <p className="text-sm font-semibold text-gray-900 truncate">
            {loading ? "Loading..." : selectedAccount ? `${selectedAccount.phoneName || "Account"} · ${selectedAccount.phoneNumber}` : "Select a phone"}
          </p>
        </div>
        <ChevronDown size={18} className={`flex-shrink-0 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && !loading && (
        <div className="absolute left-0 top-full mt-2 w-full sm:max-w-sm py-2 bg-white rounded-xl border border-gray-200 shadow-xl z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Choose number</p>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {phoneAccounts.map((account) => {
              const isSelected = account.phoneId === selectedPhoneId;
              return (
                <button
                  key={account.phoneId}
                  type="button"
                  onClick={() => handleSelect(account)}
                  className={`w-full px-4 py-3 flex items-start gap-3 transition-colors ${
                    isSelected ? "bg-emerald-50/80" : "hover:bg-gray-50"
                  }`}
                >
                  <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
                    isSelected ? "bg-emerald-100" : "bg-gray-100"
                  }`}>
                    <Phone size={16} className={isSelected ? "text-emerald-600" : "text-gray-500"} />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className={`text-sm font-semibold truncate ${isSelected ? "text-emerald-900" : "text-gray-900"}`}>
                      {account.phoneName || "Account"}
                    </p>
                    <p className="text-xs text-gray-500 font-medium mt-0.5">{account.phoneNumber}</p>
                  </div>
                  {isSelected && (
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                      <Check size={12} className="text-white" strokeWidth={2.5} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneSelector;
